import 'flatpickr/dist/themes/airbnb.css';
import moment from 'moment';
import classnames from 'classnames';
import React, { useRef, useState } from 'react';
import Flatpickr from 'flatpickr';
import { CalendarOutlined } from '@ant-design/icons';
import ReactFlatpickr, { DateTimePickerProps } from 'react-flatpickr';
import { InputGroup, InputGroupAddon } from 'reactstrap';
import './DateTimePicker.scss';

declare module 'react-flatpickr' {
  interface DateTimePickerProps {
    className: string;
  }
}

type Props = {
  value: Date;
  onChange?: (selectedDate: Date) => void;
  onClose?: (selectedDate: Date) => void;
  minDate?: Date;
  disabled?: boolean;
  enableTime?: boolean;
  dateFormat?: string;
};

const MonthSelectorElementType = 'date-time-picker-month-selector';
const createMonthSelector = (
  minDate: Date | undefined,
  fp: Flatpickr.Instance,
) => {
  const existingElem = document.getElementById(MonthSelectorElementType);
  const monthSelector = existingElem || document.createElement('div');
  monthSelector.id = MonthSelectorElementType;
  monthSelector.className = MonthSelectorElementType;
  monthSelector.innerHTML = `<div class="${MonthSelectorElementType}__year-header">${fp.currentYear}</div>`;
  moment.months().forEach((month, monthNum) => {
    const currentMonthSelected = fp.currentMonth === monthNum;
    const monthEnabled =
      !minDate ||
      moment([fp.currentYear, monthNum, 1])
        .endOf('month')
        .isSameOrAfter(moment(minDate));
    const monthElement = document.createElement('button');
    monthElement.innerText = month.slice(0, 3);
    monthElement.className = `btn btn-outline-dark btn-lg ${MonthSelectorElementType}__button`;

    if (currentMonthSelected) {
      monthElement.className += ` ${MonthSelectorElementType}__button--selected`;
    }

    if (monthEnabled) {
      monthElement.onclick = () => {
        fp.changeMonth(monthNum, false);
        destroyMonthSelector();
      };
    } else {
      monthElement.className += ` ${MonthSelectorElementType}__button--disabled`;
      monthElement.disabled = true;
    }
    monthSelector.insertBefore(monthElement, null);
  });
  if (!existingElem) {
    fp.calendarContainer.insertAdjacentElement('afterbegin', monthSelector);
  }
};
const destroyMonthSelector = () => {
  const existingElem = document.getElementById(MonthSelectorElementType);
  if (existingElem) {
    existingElem.remove();
  }
};

const DatetimePicker = ({
  value,
  onChange,
  onClose,
  minDate,
  disabled,
  dateFormat,
  enableTime,
}: Props) => {
  const flatpickrOptions: Flatpickr.Options.Options = {
    enableTime: enableTime ?? true,
    dateFormat: dateFormat ? dateFormat : 'Y-m-d h:i K',
    minuteIncrement: 1,
    minDate,
    clickOpens: false,
  };
  const ref = useRef<any>(null);
  const [opened, setOpened] = useState(false);
  const iconClasses = classnames(
    'date-time-picker__datepicker-icon',
    'fas',
    'fa-calendar-alt',
    {
      'date-time-picker__datepicker-icon--opened': opened,
    },
  );
  const onOpen: DateTimePickerProps['onOpen'] = (
    selectedDates,
    dateStr,
    fp,
  ) => {
    destroyMonthSelector();
    if (fp.monthElements && fp.monthElements[0] && fp.innerContainer) {
      fp.monthElements[0].onclick = () => {
        createMonthSelector(minDate, fp);
      };
    }
  };
  const onPickerClosed = (dates: Date[]) => {
    setOpened(false);
    onClose && onClose(dates[0]);
  };
  const openPicker = () => {
    if (!disabled) {
      setOpened(true);
      ref?.current?.flatpickr?.open();
    }
  };

  return (
    <div
      className={classnames('date-time-picker', {
        'date-time-picker--disabled': disabled,
      })}
    >
      <InputGroup className="date-time-picker__datepicker-input chatbot__input-group">
        <div onClick={openPicker} className="date-time-picker__input-wrapper">
          <ReactFlatpickr
            className="form-control"
            options={flatpickrOptions}
            ref={ref}
            value={value}
            onChange={dates => onChange && onChange(dates[0])}
            onOpen={onOpen}
            onClose={onPickerClosed}
          />
        </div>
        <InputGroupAddon
          addonType="append"
          className="date-time-picker__datepicker-button"
        >
          <CalendarOutlined onClick={openPicker} className={iconClasses} />
        </InputGroupAddon>
      </InputGroup>
    </div>
  );
};

export default DatetimePicker;
